import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {ControlValueAccessor} from '@angular/forms';

@Component({
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ControlValueAccessorWrapperBaseComponent<OuterValue, InnerValue = OuterValue>
    implements ControlValueAccessor, OnInit {

    public innerValueAccessor: ControlValueAccessor;

    protected value: OuterValue;

    private outerPropagateOnChange: (value: OuterValue) => void;
    private outerPropagateOnTouched: () => void;

    private isInnerInitialized: boolean = false;

    private hasPendingValueToWrite: boolean = false;

    public writeValue(value: OuterValue): void {
        this.value = value;

        if (this.isInnerInitialized) {
            const innerValue = this.outerValueToInnerValue(value);
            this.innerValueAccessor.writeValue(innerValue);
        }

        this.hasPendingValueToWrite = !this.isInnerInitialized;
    }

    public ngOnInit(): void {
        this.initInnerValueAccessor();
    }

    public setDisabledState(isDisabled: boolean): void {
        if (this.innerValueAccessor.setDisabledState) {
            this.innerValueAccessor.setDisabledState(isDisabled);
        }
    }

    public registerOnChange(propagateOnChange: (value: OuterValue) => void): void {
        this.outerPropagateOnChange = propagateOnChange;
    }

    public registerOnTouched(propagateOnTouched: () => void): void {
        this.outerPropagateOnTouched = propagateOnTouched;
    }

    protected propagateOnChange(value: OuterValue): void {
        if (this.outerPropagateOnChange) {
            this.outerPropagateOnChange(value);
        }
    }

    protected propagateOnTouched(): void {
        if (this.outerPropagateOnTouched) {
            this.outerPropagateOnTouched();
        }
    }

    protected innerPropagateOnChange(innerValue: InnerValue): void {
        const outerValue = this.innerValueToOuterValue(innerValue);
        const isEqual = this.isOuterValuesEqual(this.value, outerValue);
        this.value = outerValue;

        if (!isEqual) {
            this.propagateOnChange(outerValue);
        }
    }

    protected outerValueToInnerValue(outerValue: OuterValue): InnerValue {
        return outerValue as unknown as InnerValue;
    }

    protected innerValueToOuterValue(innerValue: InnerValue): OuterValue {
        return innerValue as unknown as OuterValue;
    }

    protected initInnerValueAccessor(): void {
        this.innerValueAccessor.registerOnChange((value: InnerValue) => this.innerPropagateOnChange(value));
        this.innerValueAccessor.registerOnTouched(() => this.propagateOnTouched());

        this.isInnerInitialized = true;

        if (this.hasPendingValueToWrite) {
            this.writeValue(this.value);
        }
    }

    protected isOuterValuesEqual(outerValue1: OuterValue, outerValue2: OuterValue): boolean {
        return outerValue1 === outerValue2;
    }

}
